<template>
    <a-modal
        :visible="visible"
        :title="'Grafik Ayarları - ' + widget.title"
        width="90%"
        :footer="null"
        @cancel="$emit('close')"
        class="chart-settings-modal"
        :destroyOnClose="true"
    >
        <div class="settings-container">
            <!-- Header Section -->
            <div class="header-section">
                <div class="left-actions">
                    <!-- Search Input -->
                    <a-input-search
                        v-model="searchQuery"
                        placeholder="Ara..."
                        style="width: 250px"
                        @search="handleSearch"
                        allowClear
                    />

                    <!-- Filter Button with Popover -->
                    <a-popover
                        placement="bottomLeft"
                        trigger="click"
                        v-model="filterVisible"
                        overlayClassName="table-filter-popover"
                    >
                        <template slot="content">
                            <TableFilter :fields="fields" :value="filters" @input="onFiltersUpdated" />
                        </template>
                        <SupsisButton :classNames="`${filters.length > 0 ? 'active-filters' : ''}`">
                            <i class="material-icons">filter_alt</i>
                            Filtre
                            <span v-if="filters.length > 0" class="filter-count">
                                {{ filters.length }}
                            </span>
                            <i class="material-icons clear-filters" @click.stop="clearFilters" v-if="filters.length"
                                >close</i
                            >
                        </SupsisButton>
                    </a-popover>
                </div>
            </div>

            <!-- Divider -->
            <a-divider style="margin: 12px 0" />

            <!-- Content Section -->
            <div class="content-section">
                <div class="preview-section">
                    <div v-if="!previewData" class="loading-state">
                        <a-spin />
                        <div>Grafik yükleniyor...</div>
                    </div>
                    <div v-else-if="previewData.error" class="error-state">
                        <i class="material-icons">error_outline</i>
                        <div>{{ previewData.message }}</div>
                    </div>
                    <template v-else>
                        <bar-chart
                            v-if="$_.get(previewSettings, 'chartType') === 'bar'"
                            :data="previewData"
                            :settings="previewSettings"
                            :title="widget.title"
                            :fields="fields"
                        />
                        <line-chart
                            v-else-if="
                                $_.get(previewSettings, 'chartType') === 'line' ||
                                $_.get(previewSettings, 'chartType') === 'smooth-line'
                            "
                            :data="previewData"
                            :settings="previewSettings"
                            :title="widget.title"
                            :fields="fields"
                        />
                        <stacked-bar-chart
                            v-else-if="$_.get(previewSettings, 'chartType') === 'stacked-bar'"
                            :data="previewData"
                            :settings="previewSettings"
                            :title="widget.title"
                            :fields="fields"
                        />
                        <stacked-line-chart
                            v-else-if="
                                $_.get(previewSettings, 'chartType') === 'stacked-line' ||
                                $_.get(previewSettings, 'chartType') === 'stacked-line-100'
                            "
                            :data="previewData"
                            :settings="previewSettings"
                            :title="widget.title"
                            :fields="fields"
                        />
                        <pie-chart
                            v-else-if="$_.get(previewSettings, 'chartType') === 'pie'"
                            :data="previewData"
                            :settings="previewSettings"
                            :title="widget.title"
                            :fields="fields"
                        />
                        <donut-chart
                            v-else-if="$_.get(previewSettings, 'chartType') === 'donut'"
                            :data="previewData"
                            :settings="previewSettings"
                            :title="widget.title"
                            :fields="fields"
                        />
                        <area-chart
                            v-else-if="$_.get(previewSettings, 'chartType') === 'area'"
                            :data="previewData"
                            :settings="previewSettings"
                            :title="widget.title"
                            :fields="fields"
                        />
                        <stacked-area-chart
                            v-else-if="
                                $_.get(previewSettings, 'chartType') === 'stacked-area' ||
                                $_.get(previewSettings, 'chartType') === 'stacked-area-100'
                            "
                            :data="previewData"
                            :settings="previewSettings"
                            :title="widget.title"
                            :fields="fields"
                        />
                        <bubble-chart
                            v-else-if="$_.get(previewSettings, 'chartType') === 'bubble'"
                            :data="previewData"
                            :settings="previewSettings"
                            :title="widget.title"
                            :fields="fields"
                        />
                        <!-- Diğer grafik tipleri buraya eklenecek -->
                    </template>
                </div>

                <!-- Vertical Divider -->
                <a-divider type="vertical" style="height: 100%; margin: 0 12px" />

                <div class="settings-section">
                    <a-form :form="form" layout="vertical">
                        <!-- Grafik Tipi Seçimi -->
                        <a-collapse :defaultActiveKey="['1']" class="chart-type-collapse">
                            <a-collapse-panel key="1" header="Grafik tipi">
                                <div class="chart-type-selector">
                                    <div class="chart-type-category">
                                        <div class="category-title">En popüler</div>
                                        <div class="chart-type-options">
                                            <div 
                                                class="chart-type-option" 
                                                :class="{ active: previewSettings.chartType === 'pie' }"
                                                @click="handleChartTypeChange('pie')"
                                            >
                                                <img src="/static/img/tables/pie.svg" alt="Pasta Grafiği" />
                                                <div class="option-name">Pasta</div>
                                            </div>
                                            <div 
                                                class="chart-type-option" 
                                                :class="{ active: previewSettings.chartType === 'bar' }"
                                                @click="handleChartTypeChange('bar')"
                                            >
                                                <img src="/static/img/tables/bar.svg" alt="Sütun Grafiği" />
                                                <div class="option-name">Sütun</div>
                                            </div>
                                            <div 
                                                class="chart-type-option" 
                                                :class="{ active: previewSettings.chartType === 'line' }"
                                                @click="handleChartTypeChange('line')"
                                            >
                                                <img src="/static/img/tables/line.svg" alt="Çizgi Grafiği" />
                                                <div class="option-name">Çizgi</div>
                                            </div>
                                            <div 
                                                class="chart-type-option" 
                                                :class="{ active: previewSettings.chartType === 'stacked-bar' }"
                                                @click="handleChartTypeChange('stacked-bar')"
                                            >
                                                <img src="/static/img/tables/stacked-bar.svg" alt="Yığılmış Sütun Grafiği" />
                                                <div class="option-name">Yığılmış Sütun</div>
                                            </div>
                                            <div 
                                                class="chart-type-option" 
                                                :class="{ active: previewSettings.chartType === 'bubble' }"
                                                @click="handleChartTypeChange('bubble')"
                                            >
                                                <img src="/static/img/tables/bubble.svg" alt="Kabarcık Grafiği" />
                                                <div class="option-name">Kabarcık</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="chart-type-category">
                                        <div class="category-title">Pasta</div>
                                        <div class="chart-type-options">
                                            <div 
                                                class="chart-type-option" 
                                                :class="{ active: previewSettings.chartType === 'pie' }"
                                                @click="handleChartTypeChange('pie')"
                                            >
                                                <img src="/static/img/tables/pie.svg" alt="Pasta Grafiği" />
                                                <div class="option-name">Pasta</div>
                                            </div>
                                            <div 
                                                class="chart-type-option" 
                                                :class="{ active: previewSettings.chartType === 'donut' }"
                                                @click="handleChartTypeChange('donut')"
                                            >
                                                <img src="/static/img/tables/donut.svg" alt="Halka Grafiği" />
                                                <div class="option-name">Halka</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="chart-type-category">
                                        <div class="category-title">Çizgi</div>
                                        <div class="chart-type-options">
                                            <div 
                                                class="chart-type-option" 
                                                :class="{ active: previewSettings.chartType === 'line' }"
                                                @click="handleChartTypeChange('line')"
                                            >
                                                <img src="/static/img/tables/line.svg" alt="Çizgi Grafiği" />
                                                <div class="option-name">Çizgi</div>
                                            </div>
                                            <div 
                                                class="chart-type-option" 
                                                :class="{ active: previewSettings.chartType === 'smooth-line' }"
                                                @click="handleChartTypeChange('smooth-line')"
                                            >
                                                <img src="/static/img/tables/smooth-line.svg" alt="Yumuşak Çizgi Grafiği" />
                                                <div class="option-name">Yumuşak Çizgi</div>
                                            </div>
                                            <div 
                                                class="chart-type-option" 
                                                :class="{ active: previewSettings.chartType === 'stacked-line' }"
                                                @click="handleChartTypeChange('stacked-line')"
                                            >
                                                <img src="/static/img/tables/stacked-line.svg" alt="Yığılmış Çizgi Grafiği" />
                                                <div class="option-name">Yığılmış Çizgi</div>
                                            </div>
                                            <div 
                                                class="chart-type-option d-none" 
                                                :class="{ active: previewSettings.chartType === 'stacked-line-100' }"
                                                @click="handleChartTypeChange('stacked-line-100')"
                                            >
                                                <img src="/static/img/tables/stacked-line.svg" alt="Yığılmış Çizgi %100 Grafiği" />
                                                <div class="option-name">Yığılmış Çizgi %100</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="chart-type-category">
                                        <div class="category-title">Sütun</div>
                                        <div class="chart-type-options">
                                            <div 
                                                class="chart-type-option" 
                                                :class="{ active: previewSettings.chartType === 'bar' }"
                                                @click="handleChartTypeChange('bar')"
                                            >
                                                <img src="/static/img/tables/bar.svg" alt="Sütun Grafiği" />
                                                <div class="option-name">Sütun</div>
                                            </div>
                                            <div 
                                                class="chart-type-option" 
                                                :class="{ active: previewSettings.chartType === 'stacked-bar' }"
                                                @click="handleChartTypeChange('stacked-bar')"
                                            >
                                                <img src="/static/img/tables/stacked-bar.svg" alt="Yığılmış Sütun Grafiği" />
                                                <div class="option-name">Yığılmış Sütun</div>
                                            </div>
                                            <div 
                                                class="chart-type-option d-none" 
                                                :class="{ active: previewSettings.chartType === 'stacked-bar-100' }"
                                                @click="handleChartTypeChange('stacked-bar-100')"
                                            >
                                                <img src="/static/img/tables/stacked-bar.svg" alt="Yığılmış Sütun %100 Grafiği" />
                                                <div class="option-name">Yığılmış Sütun %100</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="chart-type-category">
                                        <div class="category-title">Kolon</div>
                                        <div class="chart-type-options">
                                            <div 
                                                class="chart-type-option" 
                                                :class="{ active: previewSettings.chartType === 'bar' }"
                                                @click="handleChartTypeChange('bar')"
                                            >
                                                <img src="/static/img/tables/bar.svg" alt="Kolon Grafiği" style="transform: rotate(90deg);" />
                                                <div class="option-name">Kolon</div>
                                            </div>
                                            <div 
                                                class="chart-type-option" 
                                                :class="{ active: previewSettings.chartType === 'stacked-bar' }"
                                                @click="handleChartTypeChange('stacked-bar')"
                                            >
                                                <img src="/static/img/tables/stacked-bar.svg" alt="Yığılmış Kolon Grafiği" style="transform: rotate(90deg);" />
                                                <div class="option-name">Yığılmış Kolon</div>
                                            </div>
                                            <div 
                                                class="chart-type-option d-none" 
                                                :class="{ active: previewSettings.chartType === 'stacked-column-100' }"
                                                @click="handleChartTypeChange('stacked-column-100')"
                                            >
                                                <img src="/static/img/tables/stacked-bar.svg" alt="Yığılmış Kolon %100 Grafiği" style="transform: rotate(90deg);" />
                                                <div class="option-name">Yığılmış Kolon %100</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="chart-type-category">
                                        <div class="category-title">Alan</div>
                                        <div class="chart-type-options">
                                            <div 
                                                class="chart-type-option" 
                                                :class="{ active: previewSettings.chartType === 'area' }"
                                                @click="handleChartTypeChange('area')"
                                            >
                                                <img src="/static/img/tables/area.svg" alt="Alan Grafiği" />
                                                <div class="option-name">Alan</div>
                                            </div>
                                            <div 
                                                class="chart-type-option" 
                                                :class="{ active: previewSettings.chartType === 'stacked-area' }"
                                                @click="handleChartTypeChange('stacked-area')"
                                            >
                                                <img src="/static/img/tables/stacked-area.svg" alt="Yığılmış Alan Grafiği" />
                                                <div class="option-name">Yığılmış Alan</div>
                                            </div>
                                            <div 
                                                class="chart-type-option d-none" 
                                                :class="{ active: previewSettings.chartType === 'stacked-area-100' }"
                                                @click="handleChartTypeChange('stacked-area-100')"
                                            >
                                                <img src="/static/img/tables/stacked-area.svg" alt="Yığılmış Alan %100 Grafiği" />
                                                <div class="option-name">Yığılmış Alan %100</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="chart-type-category">
                                        <div class="category-title">Kabarcık</div>
                                        <div class="chart-type-options">
                                            <div 
                                                class="chart-type-option" 
                                                :class="{ active: previewSettings.chartType === 'bubble' }"
                                                @click="handleChartTypeChange('bubble')"
                                            >
                                                <img src="/static/img/tables/bubble.svg" alt="Kabarcık Grafiği" />
                                                <div class="option-name">Kabarcık</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </a-collapse-panel>
                        </a-collapse>

                        <!-- X Ekseni Ayarları -->
                        <a-form-item label="X Ekseni">
                            <a-select
                                :value="$_.get(previewSettings, 'xAxis.field')"
                                @change="handleXAxisFieldChange"
                                style="width: 100%"
                                placeholder="Alan seçiniz"
                            >
                                <a-select-option
                                    v-for="field in isBubbleChart ? numericFields : fields"
                                    :key="field._id"
                                    :value="field._id"
                                >
                                    {{ field.name }}
                                </a-select-option>
                            </a-select>
                        </a-form-item>

                        <!-- Bubble Size Ayarları -->
                        <a-form-item v-if="isBubbleChart" label="Kabarcık Boyutu">
                            <a-select
                                :value="$_.get(previewSettings, 'bubble.sizeField')"
                                @change="handleBubbleSizeFieldChange"
                                style="width: 100%"
                                placeholder="Sayısal alan seçiniz"
                            >
                                <a-select-option v-for="field in numericFields" :key="field._id" :value="field._id">
                                    {{ field.name }}
                                </a-select-option>
                            </a-select>
                        </a-form-item>

                        <!-- Stack By Ayarları -->
                        <a-form-item v-if="isStackedChart" class="stack-settings w-100">
                            <div class="section-label">Gruplama Alanı:</div>
                            <a-select
                                :value="$_.get(previewSettings, 'stackBy.field')"
                                @change="handleStackByFieldChange"
                                style="width: 100%"
                                placeholder="Alan seçiniz"
                            >
                                <a-select-option v-for="field in fields" :key="field._id" :value="field._id">
                                    {{ field.name }}
                                </a-select-option>
                            </a-select>

                            <template v-if="showStackModeAndExpand">
                                <div class="stack-mode-section">
                                    <div class="section-label">Gruplama Şekli:</div>
                                    <a-button-group>
                                        <a-button
                                            :type="
                                                $_.get(previewSettings, 'stackBy.stackMode', 'stacked') === 'stacked'
                                                    ? 'primary'
                                                    : 'default'
                                            "
                                            @click="handleStackModeChange('stacked')"
                                            class="d-flex justify-content-center align-items-center gap-5"
                                        >
                                            <i class="material-icons">layers</i>
                                            Üst Üste
                                        </a-button>
                                        <a-button
                                            :type="
                                                $_.get(previewSettings, 'stackBy.stackMode', 'stacked') === 'grouped'
                                                    ? 'primary'
                                                    : 'default'
                                            "
                                            @click="handleStackModeChange('grouped')"
                                            class="d-flex justify-content-center align-items-center gap-5"
                                        >
                                            <i class="material-icons">view_column</i>
                                            Yan Yana
                                        </a-button>
                                    </a-button-group>
                                </div>

                                <div class="expand-mode-section">
                                    <div class="section-label">Grafik Yönü:</div>
                                    <a-button-group>
                                        <a-button
                                            :type="
                                                $_.get(previewSettings, 'stackBy.expandMode', 'vertical') === 'vertical'
                                                    ? 'primary'
                                                    : 'default'
                                            "
                                            @click="handleExpandModeChange('vertical')"
                                            class="d-flex justify-content-center align-items-center gap-5"
                                        >
                                            <i class="material-icons">bar_chart</i>
                                            Dikey
                                        </a-button>
                                        <a-button
                                            :type="
                                                $_.get(previewSettings, 'stackBy.expandMode', 'vertical') ===
                                                'horizontal'
                                                    ? 'primary'
                                                    : 'default'
                                            "
                                            @click="handleExpandModeChange('horizontal')"
                                            class="d-flex justify-content-center align-items-center gap-5"
                                        >
                                            <i class="material-icons">show_chart</i>
                                            Yatay
                                        </a-button>
                                    </a-button-group>
                                </div>
                            </template>
                        </a-form-item>

                        <!-- Y Ekseni Ayarları -->
                        <a-form-item label="Y Ekseni">
                            <div class="y-axis-settings">
                                <a-select
                                    v-if="!isBubbleChart"
                                    :value="$_.get(previewSettings, 'yAxis.calculationType')"
                                    @change="handleYAxisCalculationTypeChange"
                                    style="width: 100%"
                                >
                                    <a-select-option value="count">Kayıt Sayısı</a-select-option>
                                    <a-select-option value="sum">Toplam</a-select-option>
                                    <a-select-option value="average">Ortalama</a-select-option>
                                    <a-select-option value="median">Medyan</a-select-option>
                                    <a-select-option value="min">Minimum</a-select-option>
                                    <a-select-option value="max">Maksimum</a-select-option>
                                </a-select>
                                <a-select
                                    v-if="
                                        !isBubbleChart && $_.get(previewSettings, 'yAxis.calculationType') !== 'count'
                                    "
                                    :value="$_.get(previewSettings, 'yAxis.field')"
                                    @change="handleYAxisFieldChange"
                                    style="width: 100%"
                                    placeholder="Sayısal alan seçiniz"
                                >
                                    <a-select-option v-for="field in numericFields" :key="field._id" :value="field._id">
                                        {{ field.name }}
                                    </a-select-option>
                                </a-select>
                                <a-select
                                    v-if="isBubbleChart"
                                    :value="$_.get(previewSettings, 'yAxis.field')"
                                    @change="handleYAxisFieldChange"
                                    style="width: 100%"
                                    placeholder="Sayısal alan seçiniz"
                                >
                                    <a-select-option v-for="field in numericFields" :key="field._id" :value="field._id">
                                        {{ field.name }}
                                    </a-select-option>
                                </a-select>
                            </div>
                        </a-form-item>

                        <!-- Tarih Gruplandırma -->
                        <a-form-item v-if="hasDateField" label="Tarih Gruplandırma">
                            <div class="date-grouping-settings">
                                <a-select
                                    :value="$_.get(previewSettings, 'dateGrouping.field')"
                                    @change="handleDateGroupingFieldChange"
                                    style="width: 100%"
                                    placeholder="Tarih alanı seçiniz"
                                >
                                    <a-select-option v-for="field in dateFields" :key="field._id" :value="field._id">
                                        {{ field.name }}
                                    </a-select-option>
                                </a-select>

                                <template v-if="$_.get(previewSettings, 'dateGrouping.field')">
                                    <div class="section-label mt-2">Gruplandırma Tipi:</div>
                                    <a-select
                                        :value="$_.get(previewSettings, 'dateGrouping.type')"
                                        @change="(value) => $_.set(previewSettings, 'dateGrouping.type', value)"
                                        style="width: 100%"
                                        placeholder="Gruplandırma seçiniz"
                                    >
                                        <a-select-option value="day">Günlük</a-select-option>
                                        <a-select-option value="week">Haftalık</a-select-option>
                                        <a-select-option value="month">Aylık</a-select-option>
                                        <a-select-option value="quarter">3 Aylık</a-select-option>
                                        <a-select-option value="year">Yıllık</a-select-option>
                                    </a-select>
                                </template>
                            </div>
                        </a-form-item>

                        <!-- Özelleştirme Ayarları -->
                        <a-collapse>
                            <a-collapse-panel header="Özelleştirme Ayarları">
                                <!-- Label ve Renk Özelleştirme -->
                                <a-form-item label="Etiket ve Renk Özelleştirme">
                                    <a-button type="dashed" block @click="showLabelCustomizationModal = true">
                                        <i class="material-icons">palette</i>
                                        Etiket ve Renkleri Özelleştir
                                    </a-button>
                                </a-form-item>

                                <!-- Sıralama -->
                                <a-form-item label="Sıralama">
                                    <a-select
                                        :value="$_.get(previewSettings, 'customization.sortBy')"
                                        @change="handleSortByChange"
                                        style="width: 100%"
                                    >
                                        <a-select-option value="y-axis-asc">Y Ekseni (Artan)</a-select-option>
                                        <a-select-option value="y-axis-desc">Y Ekseni (Azalan)</a-select-option>
                                        <a-select-option value="x-axis-asc">X Ekseni (Artan)</a-select-option>
                                        <a-select-option value="x-axis-desc">X Ekseni (Azalan)</a-select-option>
                                    </a-select>
                                </a-form-item>

                                <!-- Boş Değerler -->
                                <a-form-item>
                                    <a-checkbox
                                        :checked="$_.get(previewSettings, 'customization.showEmptyValues', false)"
                                        @change="(e) => handleShowEmptyValuesChange(e.target.checked)"
                                    >
                                        Boş değerleri göster
                                    </a-checkbox>
                                </a-form-item>

                                <!-- Gösterim Limiti Açma/Kapama -->
                                <a-form-item>
                                    <a-checkbox
                                        :checked="$_.get(previewSettings, 'customization.showOnlyEnabled', false)"
                                        @change="(e) => handleShowOnlyEnabledChange(e.target.checked)"
                                    >
                                        Gösterim limiti uygula
                                    </a-checkbox>
                                </a-form-item>

                                <!-- Top/Bottom Filtreleme -->
                                <a-form-item
                                    v-if="$_.get(previewSettings, 'customization.showOnlyEnabled')"
                                    label="Gösterim Limiti"
                                >
                                    <div class="show-only-settings">
                                        <a-select
                                            :value="$_.get(previewSettings, 'customization.showOnly.mode')"
                                            @change="handleShowOnlyModeChange"
                                            style="width: 120px"
                                        >
                                            <a-select-option value="top">İlk</a-select-option>
                                            <a-select-option value="bottom">Son</a-select-option>
                                        </a-select>
                                        <a-input-number
                                            :value="$_.get(previewSettings, 'customization.showOnly.take')"
                                            @change="handleShowOnlyTakeChange"
                                            :min="1"
                                            :max="100"
                                        />
                                    </div>
                                </a-form-item>
                            </a-collapse-panel>
                        </a-collapse>

                        <!-- Benchmark Çizgileri -->
                        <a-collapse v-if="!isPieOrDonutChart">
                            <a-collapse-panel header="Benchmark Çizgileri">
                                <div class="benchmark-lines">
                                    <div
                                        v-for="(line, index) in previewSettings.benchmarkLines"
                                        :key="index"
                                        class="benchmark-line-item"
                                    >
                                        <div class="line-row">
                                            <a-input v-model="line.label" placeholder="Etiket" style="width: 200px" />
                                            <a-input v-model="line.text" placeholder="Açıklama" style="width: 200px" />
                                        </div>
                                        <div class="line-row">
                                            <a-input-number
                                                v-model="line.value"
                                                placeholder="Değer"
                                                style="width: 150px"
                                            />
                                            <a-select v-model="line.axis" style="width: 150px">
                                                <a-select-option value="x">X Ekseni</a-select-option>
                                                <a-select-option value="y">Y Ekseni</a-select-option>
                                            </a-select>
                                            <a-input v-model="line.color" type="color" style="width: 50px" />
                                            <a-button type="link" @click="removeBenchmarkLine(index)">
                                                <i class="material-icons">delete</i>
                                            </a-button>
                                        </div>
                                    </div>
                                    <a-button type="dashed" block @click="addBenchmarkLine">
                                        <i class="material-icons">add</i>
                                        Benchmark Çizgisi Ekle
                                    </a-button>
                                </div>
                            </a-collapse-panel>
                        </a-collapse>
                    </a-form>
                </div>
            </div>

            <!-- Kaydet/İptal Butonları -->
            <div class="form-actions">
                <a-button @click="$emit('close')">İptal</a-button>
                <a-button type="primary" @click="handleSave" :loading="saving"> Kaydet </a-button>
            </div>
        </div>

        <!-- Label Özelleştirme Modalı -->
        <label-color-customization
            v-if="showLabelCustomizationModal"
            :visible.sync="showLabelCustomizationModal"
            :chart-type="previewSettings.chartType"
            :preview-data="previewData"
            :fields="fields"
            :settings="previewSettings"
            @save="handleLabelCustomizationSave"
            @change="handleLabelCustomizationChange"
            @close="showLabelCustomizationModal = false"
            @cancel="showLabelCustomizationModal = false"
        />
    </a-modal>
</template>

<script>
import Vue from "vue";
import BarChart from "./BarChart.vue";
import LineChart from "./LineChart.vue";
import AreaChart from "./AreaChart.vue";
import StackedBarChart from "./StackedBarChart.vue";
import StackedAreaChart from "./StackedAreaChart.vue";
import PieChart from "./PieChart.vue";
import DonutChart from "./DonutChart.vue";
import StackedLineChart from "./StackedLineChart.vue";
import BubbleChart from "./BubbleChart.vue";
import _ from "lodash";
import { TABLE_API } from "../../../../../../../api/table";
import LabelColorCustomization from "./LabelColorCustomization.vue";
import log2 from "../../../../../../../utils/log2";
import TableFilter from "../../../components/filter/TableFilter.vue";
import SupsisButton from "../../../../../Settings/components/SupsisButton.vue";

const log = log2("ChartSettingsModal");
export default Vue.extend({
    name: "ChartSettingsModal",
    components: {
        SupsisButton,
        BarChart,
        LineChart,
        AreaChart,
        StackedBarChart,
        StackedAreaChart,
        PieChart,
        DonutChart,
        StackedLineChart,
        BubbleChart,
        LabelColorCustomization,
        TableFilter,
    },
    props: {
        visible: {
            type: Boolean,
            required: true,
        },
        widget: {
            type: Object,
            required: true,
        },
        fields: {
            type: Array,
            required: true,
        },
        table: {
            type: Object,
            required: true,
        },
        upstreamFilters: {
            type: Object,
            default: () => ({ filters: [], searchQuery: "" }),
        },
    },
    data() {
        return {
            form: this.$form.createForm(this),
            previewSettings: _.cloneDeep(this.widget.settings || {}),
            filters: _.cloneDeep(this.widget.settings?.filters || []),
            searchQuery: _.cloneDeep(this.widget.settings?.searchQuery || ""),
            previewData: null,
            saving: false,
            showLabelCustomizationModal: false,
            savingLabels: false,
            selectedXAxisField: null,
            selectedYAxisField: null,
            filterVisible: false,
        };
    },
    computed: {
        numberFields() {
            return this.fields.filter((field) => field.valueType === "number");
        },
        isStackedChart() {
            const chartType = this.previewSettings.chartType;
            return (
                chartType &&
                (chartType === "stacked-bar" ||
                    chartType === "stacked-bar-100" ||
                    chartType === "stacked-area" ||
                    chartType === "stacked-area-100" ||
                    chartType === "stacked-line" ||
                    chartType === "stacked-line-100")
            );
        },
        isLineChart() {
            return (this.$_.get(this.previewSettings, "chartType") || "").includes("line");
        },
        numericFields() {
            return this.fields.filter((field) => ["number", "currency", "percent"].includes(field.fieldType));
        },
        previewChartOptions() {
            if (!this.previewData || this.previewData.error) return null;

            const formValues = this.form.getFieldsValue();

            // X ekseni field'ını bul
            const xAxisField = this.fields.find((f) => f._id === this.previewSettings.xAxis?.field);
            if (!xAxisField) return null;

            // Stack by field'ını bul (eğer stacked chart ise)
            const stackByField = this.isStackedChart
                ? this.fields.find((f) => f._id === this.previewSettings.stackBy?.field)
                : null;

            // Y ekseni field'ını bul
            const yAxisField =
                this.previewSettings.yAxis?.calculationType !== "count"
                    ? this.fields.find((f) => f._id === this.previewSettings.yAxis?.field)
                    : null;

            // Veriyi sırala
            let sortedData = [...this.previewData];
            if (this.previewSettings.customization?.sortBy) {
                const [axis, direction] = this.previewSettings.customization.sortBy.split("-");
                const sortOrder = direction === "asc" ? 1 : -1;

                sortedData.sort((a, b) => {
                    if (axis === "x") {
                        return sortOrder * (a[xAxisField.name] > b[xAxisField.name] ? 1 : -1);
                    } else {
                        return sortOrder * ((a.value || 0) - (b.value || 0));
                    }
                });
            }

            // Top/Bottom filtreleme
            if (this.previewSettings.customization?.showOnlyEnabled && this.previewSettings.customization?.showOnly) {
                const { mode, take } = this.previewSettings.customization.showOnly;
                if (mode === "top") {
                    sortedData = sortedData.slice(0, take);
                } else if (mode === "bottom") {
                    sortedData = sortedData.slice(-take);
                }
            }

            // X ekseni verilerini hazırla
            const xAxisData = sortedData.map((item) => {
                const value = item[xAxisField.name];
                const customization = this.previewSettings.customization?.labelCustomization?.[value];
                return customization?.label || value;
            });

            // Y ekseni etiketi
            const yAxisLabel = yAxisField
                ? `${yAxisField.name} (${this.previewSettings.yAxis.calculationType})`
                : `Kayıt Sayısı`;

            // Renk ve seri ayarları
            let colors = [];
            let series = [];

            if (this.isPieOrDonutChart) {
                // Pasta ve halka grafikleri için renk ve data hazırlığı
                series = [
                    {
                        type: this.previewSettings.chartType,
                        data: sortedData.map((item) => {
                            const value = item[xAxisField.name];
                            const customization = this.previewSettings.customization?.labelCustomization?.[value];
                            return {
                                name: customization?.label || value,
                                value: item.value,
                                itemStyle: {
                                    color: customization?.color,
                                },
                            };
                        }),
                    },
                ];
            } else if (this.isStackedChart && stackByField) {
                // Stacked grafikler için renk ve seri hazırlığı
                const stackGroups = new Set();
                sortedData.forEach((item) => {
                    Object.keys(item).forEach((key) => {
                        if (key !== xAxisField.name && key !== "value") {
                            stackGroups.add(key);
                        }
                    });
                });

                series = Array.from(stackGroups).map((group) => {
                    const customization = this.previewSettings.customization?.labelCustomization?.[group];
                    return {
                        name: customization?.label || group,
                        type: this.getSeriesType(),
                        stack: this.getStackMode(),
                        emphasis: { focus: "series" },
                        data: sortedData.map((item) => item[group] || 0),
                        itemStyle: {
                            color: customization?.color,
                        },
                    };
                });
            } else {
                // Tek serili grafikler için (bar, line, area, bubble)
                const customization = this.previewSettings.customization?.labelCustomization?.default;
                const defaultColor = customization?.color || "#1890ff";
                const defaultLabel = customization?.label || yAxisLabel;

                if (this.isBubbleChart) {
                    series = [
                        {
                            type: "scatter",
                            name: defaultLabel,
                            data: sortedData,
                            itemStyle: {
                                color: defaultColor,
                            },
                            symbolSize: function (data) {
                                return data[2] * 5; // bubble size
                            },
                        },
                    ];
                } else {
                    const seriesType = this.getSeriesType();
                    series = [
                        {
                            ...(typeof seriesType === "object" ? seriesType : { type: seriesType }),
                            name: defaultLabel,
                            data: sortedData.map((item) => item.value),
                            itemStyle: {
                                color: defaultColor,
                            },
                        },
                    ];
                }
            }

            // Benchmark çizgilerini hazırla
            const markLines = (this.previewSettings.benchmarkLines || []).map((line) => ({
                name: line.label,
                lineStyle: {
                    color: line.color,
                    type: "dashed",
                    width: 2,
                },
                label: {
                    formatter: `${line.label}${line.text ? ": " + line.text : ""}`,
                    position: "middle",
                    show: true,
                    color: line.color,
                    fontSize: 12,
                    backgroundColor: "#fff",
                    padding: [4, 8],
                },
                [line.axis === "x" ? "xAxis" : "yAxis"]: line.value,
            }));

            return {
                title: {
                    text: this.widget.title,
                    left: "center",
                },
                tooltip: {
                    trigger: this.isPieOrDonutChart ? "item" : "axis",
                    axisPointer: {
                        type: this.isLineChart ? "line" : "shadow",
                    },
                },
                legend: {
                    top: "30px",
                    type: "scroll",
                },
                grid: {
                    left: "3%",
                    right: "4%",
                    bottom: "3%",
                    top: "80px",
                    containLabel: true,
                },
                xAxis: this.isPieOrDonutChart
                    ? undefined
                    : {
                          type: "category",
                          data: xAxisData,
                          name: xAxisField.name,
                          axisLabel: {
                              interval: 0,
                              rotate: 30,
                          },
                      },
                yAxis: this.isPieOrDonutChart
                    ? undefined
                    : {
                          type: "value",
                          name: yAxisLabel,
                      },
                series: series,
                markLine: markLines.length
                    ? {
                          data: markLines,
                          silent: true,
                          symbol: ["none", "none"],
                      }
                    : undefined,
            };
        },
        getSeriesType() {
            const typeMap = {
                bar: "bar",
                line: "line",
                "smooth-line": "line",
                area: "line",
                "stacked-bar": "bar",
                "stacked-line": "line",
                "stacked-area": "line",
            };
            const type = typeMap[this.previewSettings.chartType] || "line";

            // Area tipi için areaStyle ekle
            if (this.previewSettings.chartType.includes("area")) {
                return { type, areaStyle: {} };
            }

            // Smooth line için smooth özelliği ekle
            if (this.previewSettings.chartType === "smooth-line") {
                return { type, smooth: true };
            }

            return type;
        },
        getStackMode() {
            if (!this.isStackedChart) return undefined;
            return this.previewSettings.chartType.includes("100") ? "percentage" : "normal";
        },
        stackMode: {
            get() {
                return this.$_.get(this.previewSettings, "stackBy.stackMode", "stacked");
            },
            set(value) {
                this.$_.set(this.previewSettings, "stackBy.stackMode", value);
            },
        },
        expandMode: {
            get() {
                return this.$_.get(this.previewSettings, "stackBy.expandMode", "vertical");
            },
            set(value) {
                this.$_.set(this.previewSettings, "stackBy.expandMode", value);
            },
        },
        dateFields() {
            return this.fields.filter((field) => field.fieldType === "datetime");
        },
        hasDateField() {
            return (
                this.dateFields.length > 0 &&
                (this.selectedXAxisField?.fieldType === "datetime" ||
                    (this.selectedYAxisField?.fieldType === "datetime" &&
                        this.previewSettings.yAxis.calculationType !== "count"))
            );
        },
        showStackModeAndExpand() {
            const chartType = this.previewSettings.chartType;
            return chartType && (chartType === "stacked-bar" || chartType === "stacked-bar-100");
        },
        isPieOrDonutChart() {
            const chartType = this.previewSettings.chartType;
            return chartType === "pie" || chartType === "donut";
        },
        isBubbleChart() {
            return this.previewSettings.chartType === "bubble";
        },
    },
    methods: {
        updateSettings(key, value) {
            this.$_.set(this.previewSettings, key, value);
            // Deep clone yaparak değişikliği tetikle
            this.previewSettings = _.cloneDeep(this.previewSettings);
            this.getChartData();
        },
        getChartData: _.debounce(async function() {
            try {
                if (!this.previewSettings.chartType) return;
                const customWidgetSettings = this.generateCustomWidgetSettings();
                this.previewData = await TABLE_API.getChartData(
                    this.table._id,
                    this.widget._id,
                    this.upstreamFilters,
                    customWidgetSettings
                );
            } catch (error) {
                console.error("Grafik verisi yüklenirken hata:", error);
            }
        }, 500),
        handleYAxisCalculationTypeChange(value) {
            if (value === "count") {
                this.updateSettings("yAxis", {
                    calculationType: value,
                    field: null,
                });
            } else {
                this.updateSettings("yAxis.calculationType", value);
            }
        },
        handleBubbleSizeFieldChange(value) {
            this.updateSettings("bubble.sizeField", value);
        },
        handleStackByFieldChange(value) {
            this.updateSettings("stackBy.field", value);
        },
        handleChartTypeChange(value) {
            this.updateSettings("chartType", value);
            // Grafik tipi değiştiğinde varsayılan ayarları uygula
            if (value === "pie" || value === "donut") {
                // Eğer mevcut bir Y ekseni ayarı yoksa varsayılan olarak count ekleyelim
                if (!this.$_.get(this.previewSettings, "yAxis.calculationType")) {
                    this.updateSettings("yAxis", { calculationType: "count" });
                }
            } else if (value === "bubble") {
                const currentYAxisField = this.$_.get(this.previewSettings, "yAxis.field");
                this.updateSettings("yAxis", { field: currentYAxisField });
            } else if (!this.$_.get(this.previewSettings, "yAxis.calculationType")) {
                this.updateSettings("yAxis", { calculationType: "count" });
            }
        },
        handleXAxisFieldChange(value) {
            this.selectedXAxisField = this.fields.find((f) => f._id === value);
            this.updateSettings("xAxis.field", value);

            // Eğer seçilen alan tarih değilse ve bu alan için tarih gruplandırma varsa, temizle
            if (
                this.selectedXAxisField?.fieldType !== "datetime" &&
                this.$_.get(this.previewSettings, "dateGrouping.field") === value
            ) {
                this.updateSettings("dateGrouping", {});
            }
        },
        handleYAxisFieldChange(value) {
            this.selectedYAxisField = this.fields.find((f) => f._id === value);
            this.updateSettings("yAxis.field", value);

            // Eğer seçilen alan tarih değilse ve bu alan için tarih gruplandırma varsa, temizle
            if (
                this.selectedYAxisField?.fieldType !== "datetime" &&
                this.$_.get(this.previewSettings, "dateGrouping.field") === value
            ) {
                this.updateSettings("dateGrouping", {});
            }
        },
        handleDateGroupingFieldChange(value) {
            if (!value) {
                this.updateSettings("dateGrouping", {});
            } else {
                this.updateSettings("dateGrouping", {
                    field: value,
                    type: "month", // Varsayılan olarak aylık gruplandırma
                });
            }
        },
        handleStackModeChange(mode) {
            this.updateSettings("stackBy.stackMode", mode);
        },
        handleExpandModeChange(mode) {
            this.updateSettings("stackBy.expandMode", mode);
        },
        handleSortByChange(value) {
            this.updateSettings("customization.sortBy", value);
        },
        handleShowEmptyValuesChange(checked) {
            this.updateSettings("customization.showEmptyValues", checked);
        },
        handleShowOnlyEnabledChange(checked) {
            this.updateSettings("customization.showOnlyEnabled", checked);
            if (!checked) {
                this.updateSettings("customization.showOnly", null);
            } else {
                this.updateSettings("customization.showOnly", {
                    mode: "top",
                    take: 10,
                });
            }
        },
        handleShowOnlyModeChange(value) {
            this.updateSettings("customization.showOnly.mode", value);
        },
        handleShowOnlyTakeChange(value) {
            this.updateSettings("customization.showOnly.take", value);
        },
        addBenchmarkLine() {
            if (!this.previewSettings.benchmarkLines) {
                this.previewSettings.benchmarkLines = [];
            }
            this.previewSettings.benchmarkLines.push({
                label: "",
                text: "",
                value: 0,
                axis: this.isLineChart ? "y" : undefined,
                color: "#ff0000",
            });
        },
        removeBenchmarkLine(index) {
            this.previewSettings.benchmarkLines.splice(index, 1);
        },
        async handleSave() {
            try {
                this.saving = true;
                const updatedWidget = {
                    ...this.widget.settings,
                    ...this.previewSettings,
                    filters: this.filters,
                    searchQuery: this.searchQuery,
                };
                console.log("updatedWidget", updatedWidget);
                await this.$emit("save", updatedWidget);
                this.$emit("close");
            } catch (error) {
                this.$message.error("Ayarlar kaydedilirken bir hata oluştu");
            } finally {
                this.saving = false;
            }
        },
        generateCustomWidgetSettings() {
            return {
                ...this.previewSettings,
                filters: this.filters,
                searchQuery: this.searchQuery,
            };
        },
        async handleLabelCustomizationSave(customization) {
            try {
                if (!this.previewSettings.customization) {
                    this.previewSettings.customization = {};
                }
                const isChanged = !_.isEqual(this.previewSettings.customization.labelCustomization, customization);
                if (!isChanged) {
                    return;
                }
                this.previewSettings.customization.labelCustomization = customization;
                await this.getChartData();

                // Modal'ı kapat
                this.showLabelCustomizationModal = false;

                // Değişiklikleri emit et
                this.$emit("settings-updated", this.previewSettings);
            } catch (error) {
                console.error("Etiket özelleştirmeleri kaydedilirken hata:", error);
                this.$message.error("Etiket özelleştirmeleri kaydedilirken bir hata oluştu");
            }
        },
        handleLabelCustomizationChange(customization) {
            log("handleLabelCustomizationChange", customization);
            if (!this.previewSettings.customization) {
                this.previewSettings.customization = {};
            }
            const isChanged = !_.isEqual(this.previewSettings.customization.labelCustomization, customization);
            if (!isChanged) {
                return;
            }
            this.previewSettings.customization.labelCustomization = customization;
        },
        onFiltersUpdated(newFilters) {
            this.filters = newFilters;
            this.getChartData();
        },
        clearFilters() {
            this.filters = [];
            this.getChartData();
        },
        handleSearch(value) {
            this.searchQuery = value;
            this.getChartData();
        },
    },
    async created() {
        try {
            this.previewSettings = _.cloneDeep(this.widget.settings || {});
            this.filters = _.cloneDeep(this.widget.settings?.filters || []);
            this.searchQuery = _.cloneDeep(this.widget.settings?.searchQuery || "");
            await this.getChartData();
        } catch (error) {
            console.error("Grafik verisi yüklenirken hata:", error);
        }
    },
    watch: {
        previewSettings: {
            handler: async function () {
                await this.getChartData();
            },
            deep: true,
            immediate: true,
        },
        "previewSettings.chartType": {
            handler(newType) {
                if (newType === "pie" || newType === "donut") {
                    // Pie ve donut grafikleri için Y ekseni ayarlarını koruyalım
                    // Eğer mevcut bir Y ekseni ayarı yoksa varsayılan olarak count ekleyelim
                    if (!this.$_.get(this.previewSettings, "yAxis")) {
                        this.$_.set(this.previewSettings, "yAxis", {
                            calculationType: "count",
                        });
                    }
                } else if (newType === "bubble") {
                    // Bubble chart için varsayılan ayarlar
                    // Y ekseni field'ını koruyoruz, sadece calculationType'ı kaldırıyoruz
                    const currentYAxisField = this.$_.get(this.previewSettings, "yAxis.field");
                    this.$_.set(this.previewSettings, "yAxis", {
                        field: currentYAxisField,
                    });
                } else {
                    // Diğer grafikler için varsayılan ayar
                    if (!this.$_.get(this.previewSettings, "yAxis.calculationType")) {
                        this.$_.set(this.previewSettings, "yAxis", {
                            calculationType: "count",
                        });
                    }
                }
            },
            immediate: true,
        },
        upstreamFilters: {
            handler: async function () {
                await this.getChartData();
            },
            deep: true,
        },
        searchQuery: function () {
            this.getChartData();
        },
    },
});
</script>

<style lang="scss">
.chart-settings-modal {
    .settings-container {
        display: flex;
        flex-direction: column;
        height: calc(90vh - 150px);

        .header-section {
            padding: 0 0 12px 0;
            .left-actions {
                display: flex;
                gap: 12px;
                align-items: center;
            }
        }

        .content-section {
            display: flex;
            flex: 1;
            min-height: 0;

            .preview-section {
                flex: 1;
                min-width: 0;
                background: #fff;
                border: 1px solid #e8e8e8;
                border-radius: 4px;
                padding: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: 100%;
                overflow: auto;

                .loading-state,
                .error-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;
                    color: rgba(0, 0, 0, 0.45);

                    .material-icons {
                        font-size: 24px;
                    }
                }
            }

            .settings-section {
                width: 400px;
                overflow-y: auto;
                padding: 16px;
                background: #fff;
                border: 1px solid #e8e8e8;
                border-radius: 4px;
            }
        }
    }

    .filter-count {
        margin-left: 4px;
        background-color: rgba(0, 0, 0, 0.2);
        padding: 2px 4px;
        border-radius: 4px;
        font-size: 12px;
        display: inline-flex;
        align-items: center;
    }

    .clear-filters {
        font-size: 14px;
        margin-left: 2px;
        cursor: pointer;
    }
    .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        padding: 12px 0;
    }

    // Grafik tipi seçimi için stiller
    .chart-type-collapse {
        margin-bottom: 16px;
        
        .ant-collapse-header {
            font-weight: 500;
        }
    }

    .chart-type-selector {
        .chart-type-category {
            margin-bottom: 20px;
            
            .category-title {
                font-weight: 500;
                margin-bottom: 10px;
                color: rgba(0, 0, 0, 0.85);
            }
            
            .chart-type-options {
                display: flex;
                flex-wrap: wrap;
                gap: 12px;
                
                .chart-type-option {
                    width: 100px;
                    height: 90px;
                    border: 1px solid #e8e8e8;
                    border-radius: 4px;
                    padding: 8px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.3s;
                    
                    &:hover {
                        border-color: #1890ff;
                        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
                    }
                    
                    &.active {
                        border-color: #1890ff;
                        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
                        background-color: rgba(24, 144, 255, 0.05);
                    }
                    
                    img {
                        width: 50px;
                        height: 50px;
                        object-fit: contain;
                    }
                    
                    .option-name {
                        margin-top: 8px;
                        font-size: 12px;
                        text-align: center;
                    }
                }
            }
        }
    }
}
</style>
